import _ from 'lodash';
import { isEmptyVal } from '@/utils';
import { getPartnerRefer, getTrackingPlanNames } from '@/views/config/partner-refer/helper/utils';
import {
  checkEligibleForPartner,
  checkPartnerReferWithZmCoupon,
  getEligiblePlanCodeListForPartner,
  getMessageStringByRules,
} from '@/views/config/partner-refer/helper/validator';
import { logger } from '@/utils/log';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';
import { validateCoupon } from '@/apis/coupon';
import { pageConfig } from '@/common/service/page-config';
import { couponParamsGeneratorInstance } from '@/views/config/coupon/helper/generate-params';

const state = {
  // just reuse coupon validation rule to check partner referral (status)
  referralCouponStatus: couponFetchStatus.idle,
  lastShowInEligibleMsg: false,
};

const getters = {
  /**
   * @description partner refer meta information
   * @return {PartnerReferBean}
   */
  partnerRefer: () => getPartnerRefer(),
  /**
   * @description coupon ui configuration, contain shopping cart or plan card view
   * @returns {CouponConfigBean}
   */
  couponConfiguration: (_, getters) => getters.partnerRefer?.features?.couponConfig,
  // eligible plan code for partner refer code
  eligiblePlanCodeListForPartner: () => getEligiblePlanCodeListForPartner(),
  // ineligible error object for partner refer code
  inEligibleErrorObjectForPartner: (state, getters) => checkEligibleForPartner(getters.eligiblePlanCodeListForPartner),
  partnerCheckLoading: (state, getters) => {
    const partner = getPartnerRefer();

    if (partner.couponCode && partner.rules?.validateByCoupon) {
      return getters.verifyCouponStatus === couponFetchStatus.pending;
    }

    return false;
  },
  // Whether the partner refer is validated
  isEligibleForPartner: (state, getters) => isEmptyVal(getters.inEligibleErrorObjectForPartner),
  // show ineligible partner refer message
  showInEligibleMsg: (state, getters) => !getters.eligiblePartnerReferOffer,
  eligiblePartnerReferOffer: (state, getters) => {
    // not meet partner refer check rules
    if (!getters.isEligibleForPartner) {
      return false;
    }

    // partner refer check valid, next check with coupon case
    const checkWithZmCouponResult = checkPartnerReferWithZmCoupon(getters.couponState);

    if (getters.isEligibleForPartner && !checkWithZmCouponResult) {
      logger.error('partner refer not allow use zoom coupon');
      return false;
    }

    return true;
  },
  isFromPartnerRefer: (state, getters) => isEmptyVal(getters.partnerRefer) === false,
  partnerReferProductNames: (state, getters) => {
    /** @type {PartnerReferBean} */
    const partnerRefer = getters.partnerRefer;
    let planCodeList = [];

    if (getters.eligiblePlanCodeListForPartner?.length) {
      planCodeList = getters.eligiblePlanCodeListForPartner;
    } else {
      const eligiblePlanList = partnerRefer.rules?.eligiblePlanList;
      const needPlanNameList = isEmptyVal(eligiblePlanList) ? _.keys(getters.getSelectedPlansWithQuantityMap) : eligiblePlanList;

      planCodeList = _.chain(needPlanNameList)
        .flatten()
        .uniq()
        .value();
    }

    return getTrackingPlanNames(planCodeList, getters.groupByPlanCodePlanMap);
  },
  promoExternalProduct: (state, getters) => {
    return _.find(getters.planMapFlat, (p) => {
      return p?.externalProduct && _.includes(getters.partnerRefer?.preloadPlan, p.plan_code);
    });
  },
  referralCouponStatus: (state) => state.referralCouponStatus,
  partnerMessageLoading: (state, getters) => {
    /**@type {PartnerReferBean} */
    const partnerRefer = getters.partnerRefer;
    const { validateByCoupon, validateByReferralCoupon } = partnerRefer?.rules || {};

    if (
      (validateByCoupon && getters.verifyCouponStatus === couponFetchStatus.pending) ||
      (validateByReferralCoupon && getters.referralCouponStatus === couponFetchStatus.pending)
    ) {
      return true;
    }

    return false;
  },
  ignoreCheckPartner: (state, getters) => { // exist partner referral meta info, but the status is invalid, will ignore check
    return !isEmptyVal(getters.partnerRefer) && !isEmptyVal(pageConfig?.partnerReferralErrorResult);
  },
  lastShowInEligibleMsg: (state) => state.lastShowInEligibleMsg,
  partnerFeaturesDependOnRules: (state, getters) => {
    const planCardHeader = getMessageStringByRules(getters.partnerRefer?.planCardHeader);
    const cartBottomBannerContent = getMessageStringByRules(getters.partnerRefer?.cartBottomBannerContent);
    const cartBottomSpecialOfferContent = getMessageStringByRules(getters.partnerRefer?.cartBottomSpecialOfferContent);

    return {
      usePlanCardMode: !isEmptyVal(planCardHeader),
      useCustomCartMode: !isEmptyVal(cartBottomBannerContent) || !isEmptyVal(cartBottomSpecialOfferContent),
    };
  },
};

const mutations = {
  setLastShowInEligibleMsg(state, data) {
    state.lastShowInEligibleMsg = data;
  },
  setReferralCouponStatus(state, status) {
    state.referralCouponStatus = status;
  },
};

const actions = {
  // check partner referral status by fake coupon (just use coupon service validation)
  async preCheckPartnerByReferralCoupon({ getters, commit }) {
    try {
      const { referralCouponCode = '' } = getters.partnerRefer;
      if (isEmptyVal(referralCouponCode)) return Promise.resolve();
      if (getters.ignoreCheckPartner) return Promise.resolve();

      commit('setLastShowInEligibleMsg', getters.showInEligibleMsg);
      commit('setReferralCouponStatus', couponFetchStatus.pending);

      const params = couponParamsGeneratorInstance.generatePureValidateCouponParams({ couponCode: referralCouponCode });

      await validateCoupon(params);

      commit('setReferralCouponStatus', couponFetchStatus.successfully);
    } catch (couponResult) {
      logger.error('validate referral coupon api', couponResult);
      commit('setReferralCouponStatus', couponFetchStatus.failure);
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
